<template>
  <div v-if="mask" v-maska="mask" style="width: 100%">
    <PInput
      :model-value="maskedDisplay"
      :label="campo.label"
      type="text"
      :maxlength="campo.tamanho ?? undefined"
      :error="error"
      :show-error="Boolean(error)"
      :required="campo.obrigatorio"
      style="width: 100%"
      :placeholder="campo.placeholder"
      @update:model-value="emitMaskedNumber"
    />
  </div>

  <PInput
    v-else
    :model-value="modelValue"
    :label="campo.label"
    type="number"
    :maxlength="campo.tamanho ?? undefined"
    :error="error"
    :show-error="Boolean(error)"
    :required="campo.obrigatorio"
    style="width: 100%"
    :placeholder="campo.placeholder"
    @update:model-value="emitNumber"
  />
</template>

<script setup lang="ts">
import { PInput } from "@f360-dev/f360-prisma";
import { computed, ref, watch } from "vue";
import { TIntegracaoCampoModel } from "../../../@types/IntegracaoCampoModel";
import { EMascaraNomeada } from "../../../@types/IntegracaoCampoEnum";
import useNamedMask from "../../../composables/dynamicForm/handlers/useNamedMask/useNamedMask";

const props = defineProps<{
  campo: TIntegracaoCampoModel;
  modelValue: number | null;
  error?: string;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: number | null): void;
}>();

const { vMaska, resolveMask } = useNamedMask();

const mask = computed(() => resolveMask(props.campo.mascara));
const isPercentageMask = computed(
  () => props.campo.mascara === EMascaraNomeada.Percentage
);

const formatLocalized = (
  value: number | null,
  formatAsPercentage = false
): string => {
  if (value === null || value === undefined) return "";

  return formatAsPercentage
    ? `${value.toFixed(2).replace(".", ",")}%`
    : String(value).replace(".", ",");
};

const parseLocalized = (value: string): number | null => {
  const normalized = value
    .replace(/[^\d,.-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".")
    .trim();

  if (!normalized) return null;

  const parsed = Number(normalized);

  return Number.isNaN(parsed) ? null : parsed;
};

const maskedDisplay = ref(
  formatLocalized(props.modelValue, isPercentageMask.value)
);

watch(
  () => props.modelValue,
  (value) => {
    if (parseLocalized(maskedDisplay.value) !== value) {
      maskedDisplay.value = formatLocalized(value, isPercentageMask.value);
    }
  }
);

const emitMaskedNumber = (value: string | number | null | undefined) => {
  const raw = value === null || value === undefined ? "" : String(value);
  maskedDisplay.value = raw;

  emit("update:modelValue", parseLocalized(raw));
};

const emitNumber = (value: number | string | null | undefined) => {
  if (value === null || value === undefined || value === "") {
    emit("update:modelValue", null);

    return;
  }

  emit("update:modelValue", typeof value === "number" ? value : Number(value));
};
</script>
